import styled from '@emotion/styled';
import { FC, RefObject, useEffect, useRef } from 'react';

import Legal from 'app/components/v2/components/Text/Legal';
import styles from 'app/components/v2/constants/styles';

import SmallBody from '../../Text/SmallBody';
import { TypeAheadOption } from '..';

// Local Typings
interface MenuItemProps {
  selected: boolean;
}
interface Props extends MenuItemProps {
  onSelect: (option: TypeAheadOption) => void;
  option: TypeAheadOption;
}

// Local Variables
function ensureItemVisibility(ref: RefObject<HTMLLIElement | null>) {
  if (!ref) {
    return;
  }

  const { current: el } = ref;
  if (el?.offsetParent) {
    const top = el.offsetTop;
    const bottom = el.offsetHeight + el.offsetTop;
    const parentHeight = el.offsetParent.clientHeight;
    const parentTop = el.offsetParent.scrollTop;

    if (bottom > parentHeight || top < parentTop) {
      el.scrollIntoView();
    }
  }
}

// Component Definition
const MenuItem = styled.li<MenuItemProps>(props => ({
  // eslint-disable-next-line @typescript-eslint/naming-convention
  '&:focus': {
    backgroundColor: props.theme.semanticColors.menu_item_focus_background,
  },
  // eslint-disable-next-line @typescript-eslint/naming-convention
  '&:hover': {
    backgroundColor: props.theme.semanticColors.menu_item_focus_background,
  },

  backgroundColor: props.selected
    ? props.theme.semanticColors.menu_item_selected_background
    : props.theme.semanticColors.card_background,
  cursor: 'pointer',
  outline: 'none',
  padding: styles.padding.sm,
}));

const ListItem: FC<Props> = props => {
  const listItemRef = useRef<HTMLLIElement>(null);
  const value = props.option.id ?? props.option.label;

  const handleClick = (opt: TypeAheadOption) => () => {
    props.onSelect(opt);
  };

  useEffect(() => {
    if (props.selected) {
      ensureItemVisibility(listItemRef);
    }
  });

  return (
    <MenuItem
      className="typeahead_dropdown_list-item"
      key={value}
      onClick={handleClick(props.option)}
      ref={listItemRef}
      selected={props.selected}
      tabIndex={0}
      value={value}
    >
      <SmallBody>
        {props.option.label}
      </SmallBody>
      {props.option.sublabel && (
        <Legal fontWeight="bold">
          {props.option.sublabel}
        </Legal>
      )}
    </MenuItem>
  );
};

export default ListItem;
